import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { bulkSetTypeAvailabilityByNames } from "../api/TypeAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Layers,
  CheckCircle,
  XCircle,
  Save,
  AlertCircle,
} from "lucide-react";

const parseTypeNames = (text) => {
  const seen = new Set();
  const names = [];
  for (const raw of String(text || "").split(/[\r\n,]+/)) {
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
};

const BulkTypeAvailabilityPage = () => {
  const navigate = useNavigate();
  const [namesText, setNamesText] = useState("");
  const [isAvailable, setIsAvailable] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const names = parseTypeNames(namesText);
    if (names.length === 0) {
      toast.warning("اكتب اسم نوع واحد على الأقل");
      return;
    }

    const statusLabel = isAvailable ? "متاح" : "غير متاح";
    if (
      !window.confirm(
        `تعيين ${names.length} نوع كـ ${statusLabel}؟`
      )
    ) {
      return;
    }

    try {
      setIsSaving(true);
      setResult(null);
      const data = await bulkSetTypeAvailabilityByNames(names, isAvailable);
      setResult(data);

      if ((data.updatedCount ?? 0) > 0) {
        toast.success(`تم تحديث ${data.updatedCount} نوع إلى ${statusLabel}`);
      }
      if (data.notFound?.length) {
        toast.warning(`${data.notFound.length} نوع لم يتم العثور عليه`);
      }
      if ((data.updatedCount ?? 0) === 0) {
        toast.error("لم يتم العثور على أي نوع مطابق");
      }
    } catch (err) {
      console.error("Bulk availability by names failed:", err);
      toast.error(err.message || "فشل تحديث التوافر");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Layers className="text-purple-600" size={32} />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                تحديث توافر الأنواع
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                اكتب أسماء أنواع الموبايل وحدّث حالتها دفعة واحدة
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/brands")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للعلامات التجارية
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 space-y-6"
        >
          <div>
            <label className="block text-right font-bold text-gray-800 mb-2">
              أنواع الموبايل
            </label>
            <textarea
              value={namesText}
              onChange={(e) => setNamesText(e.target.value)}
              rows={10}
              placeholder={"اكتب كل نوع في سطر، مثال:\niPhone 13\niPhone 14 Pro\nSamsung S23"}
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all font-mono text-sm"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-2">
              يمكن الفصل بأسطر أو بفواصل. البحث غير حساس لحالة الأحرف.
            </p>
          </div>

          <div>
            <p className="font-bold text-gray-800 mb-3">الحالة المطلوبة</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setIsAvailable(true)}
                disabled={isSaving}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold border-2 transition ${
                  isAvailable
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-green-700 border-green-200 hover:bg-green-50"
                }`}
              >
                <CheckCircle size={18} />
                متاح
              </button>
              <button
                type="button"
                onClick={() => setIsAvailable(false)}
                disabled={isSaving}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold border-2 transition ${
                  !isAvailable
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-red-700 border-red-200 hover:bg-red-50"
                }`}
              >
                <XCircle size={18} />
                غير متاح
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:scale-[1.01] transition disabled:opacity-50"
          >
            <Save size={20} />
            {isSaving ? "جاري التحديث..." : "تأكيد التحديث"}
          </button>
        </form>

        {result && (
          <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-purple-600" size={22} />
              <h2 className="text-xl font-bold text-gray-800">نتيجة التحديث</h2>
            </div>

            <p className="text-gray-700">
              تم تحديث <span className="font-bold text-purple-700">{result.updatedCount ?? 0}</span> نوع
              إلى{" "}
              <span className={result.isAvailable ? "font-bold text-green-700" : "font-bold text-red-700"}>
                {result.isAvailable ? "متاح" : "غير متاح"}
              </span>
            </p>

            {result.updated?.length > 0 && (
              <div>
                <p className="font-bold text-gray-700 mb-2">الأنواع المحدّثة:</p>
                <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
                  {result.updated.map((item) => (
                    <li key={item.typeId} className="px-3 py-2 text-sm flex justify-between gap-2">
                      <span className="font-medium text-gray-800">{item.name}</span>
                      <span className="text-gray-500 shrink-0">
                        {item.brandName || `Brand #${item.brandId}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.notFound?.length > 0 && (
              <div>
                <p className="font-bold text-red-700 mb-2">لم يتم العثور عليها:</p>
                <ul className="max-h-40 overflow-y-auto space-y-1">
                  {result.notFound.map((name) => (
                    <li key={name} className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTypeAvailabilityPage;
